import React, { useMemo } from 'react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { HTTPError } from 'ky';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ApiResponse, CpsNotificationProps } from '@cs-cms/types';
import { useNotificationContext } from '../hooks/useNotificationContext';

interface QueryProviderWrapperProps {
  children: React.ReactNode;
  appInsights?: ApplicationInsights;
  graphSharesEndpoint?: string;
}

const shouldIgnoreError = (errorResponse: Response, graphSharesEndpoint?: string): boolean => {
  if (!graphSharesEndpoint) {
    return false;
  }
  return errorResponse.status === 403 && errorResponse.url.includes(graphSharesEndpoint);
};

const getCorrelationId = async (errorResponse: Response): Promise<string | null> => {
  try {
    const response = (await errorResponse.json()) as ApiResponse;
    return response.errorDetails?.operationId ?? null;
  } catch {
    return null;
  }
};

const handle500Error = async (
  errorResponse: Response,
  showToast: (props: Omit<CpsNotificationProps, 'show'>) => void,
  appInsights?: ApplicationInsights
): Promise<void> => {
  const errorMessage = 'Something bad happened';
  const correlationId = await getCorrelationId(errorResponse);
  const fullMessage = correlationId ? `${errorMessage} (Correlation ID: ${correlationId})` : errorMessage;

  showToast({ type: 'error', message: fullMessage });
  appInsights?.trackException({
    error: new Error(`FRONTEND: ${errorMessage}`),
    severityLevel: SeverityLevel.Error
  });
};

const handle4xxError = (error: Error, appInsights?: ApplicationInsights): void => {
  const errorMessage = `Something went wrong: ${error.message}`;
  appInsights?.trackException({
    error: new Error(`FRONTEND: ${errorMessage}`),
    severityLevel: SeverityLevel.Error
  });
};

const handleHTTPError = async (
  error: HTTPError,
  showToast: (props: Omit<CpsNotificationProps, 'show'>) => void,
  appInsights?: ApplicationInsights,
  graphSharesEndpoint?: string
): Promise<void> => {
  const { response: errorResponse } = error;

  if (!errorResponse) {
    return;
  }

  if (shouldIgnoreError(errorResponse, graphSharesEndpoint)) {
    return;
  }

  if (errorResponse.status === 500) {
    await handle500Error(errorResponse, showToast, appInsights);
    return;
  }

  if (errorResponse.status >= 400 && errorResponse.status < 500) {
    handle4xxError(error, appInsights);
  }
};

const handleGenericError = (
  query: any,
  showToast: (props: Omit<CpsNotificationProps, 'show'>) => void,
  appInsights?: ApplicationInsights
): void => {
  const errorMessage = `Something went wrong for query: ${query.queryKey}`;
  showToast({ type: 'error', message: errorMessage });
  appInsights?.trackException({
    error: new Error(`FRONTEND: ${errorMessage}`),
    severityLevel: SeverityLevel.Error
  });
};

export const QueryProviderWrapper: React.FC<QueryProviderWrapperProps> = ({
  children,
  appInsights,
  graphSharesEndpoint
}) => {
  const { showToast } = useNotificationContext();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 2000
          }
        },
        queryCache: new QueryCache({
          onError: async (error, query) => {
            if (!query.state.error) {
              return;
            }

            if (error instanceof Error) {
              await handleHTTPError(error as HTTPError, showToast, appInsights, graphSharesEndpoint);
            } else {
              handleGenericError(query, showToast, appInsights);
            }
          }
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            if (mutation.options.onError) {
              return;
            }
            if (error instanceof Error) {
              const errorMessage = `Something went wrong: ${error.message}`;
              showToast({ type: 'error', message: errorMessage });
              appInsights?.trackException({
                error: new Error(`FRONTEND: ${errorMessage}`),
                severityLevel: SeverityLevel.Error
              });
            } else {
              const errorMessage = `Something went wrong: ${error}`;
              appInsights?.trackException({
                error: new Error(`FRONTEND: ${errorMessage}`),
                severityLevel: SeverityLevel.Error
              });
            }
          }
        })
      }),
    [showToast, appInsights, graphSharesEndpoint]
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* By default, React Query Devtools are only included in bundles when process.env.NODE_ENV === 'development' */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};
